import React from 'react';
import { styled } from '@mui/material/styles';
import Button, { ButtonProps } from '@mui/material/Button';

import { ReactComponent as ArrowIcon } from 'assets/images/arrow-btn-second.svg';
import { ReactComponent as ArrowDownloadIcon } from 'assets/images/arrow-btn-download.svg';
import { ReactComponent as ArrowDisabledIcon } from 'assets/images/arrow-btn-disabled.svg';

type StyledButtonProps = {
  fontSize?: string;
  size?: 'small' | 'medium';
  outlined?: boolean;
  downArrow?: boolean;
} & ButtonProps;

export default function ButtonWithArrowIcon({
  fontSize,
  size,
  outlined = false,
  downArrow = false,
  ...props
}: StyledButtonProps) {
  const StyledComponent = styled(Button)<ButtonProps>(({ theme }) => ({
    borderRadius: '1px',
    textDecoration: 'none',
    textTransform: 'capitalize',
    color: outlined
      ? theme.palette?.text.primary
      : theme.palette?.primary.contrastText,
    border: outlined ? '1px solid #D0D3D6' : 'unset',
    background: outlined ? 'transparent' : theme.palette?.primary.main,
    boxShadow: 'none',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: fontSize ?? '14px',
    lineHeight: '100%',
    letterSpacing: '0.01em',
    zIndex: 2,
    minWidth: '108px',
    minHeight: size === 'small' ? '28px' : '38px',
    paddingLeft: '14px',
    paddingRight: '14px',
    justifyContent: 'space-between',
    '&[href]': {
      textDecoration: 'none',
      textDecorationLine: 'none',
      color: outlined
        ? theme.palette?.text.primary
        : theme.palette?.primary.contrastText,
    },
    '&:hover': {
      color: outlined
        ? theme.palette?.text.primary
        : theme.palette?.primary.contrastText,
      borderColor: outlined ? '#D0D3D6' : 'rgba(0, 0, 0, 0.25)',
      boxShadow: 'none',
      background: outlined ? '#F0F0F1' : theme.palette?.primary.main,
    },
    '&::after': {
      borderColor: 'none',
      boxShadow: 'none',
      background: theme.palette?.primary.main,
    },
    '& svg': {
      width: '14px',
      height: downArrow ? '11px' : '8px',
      marginLeft: size === 'medium' ? '25px' : '20px',
      path: {
        fill: outlined
          ? theme.palette?.text.primary
          : theme.palette?.primary.contrastText,
      },
    },
    '&:disabled': {
      color: '#D0D3D6',
      background: 'transparent',
      border: '1px solid #D0D3D6',
      borderRadius: '1px',
      '& svg': {
        marginLeft: '25px',
        marginTop: downArrow ? 'unset' : '4px',
        filter: downArrow ? 'brightness(0) invert(0.7)' : 'unset',
        path: {
          fill: '#D0D3D6',
        },
      },
    },
  }));

  return (
    <StyledComponent {...props}>
      {props.children}
      {!downArrow && (!props.disabled ? <ArrowIcon /> : <ArrowDisabledIcon />)}
      {downArrow && <ArrowDownloadIcon />}
    </StyledComponent>
  );
}
